<script setup lang="ts">
    import type { PayrollDataField } from '@/types';
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';
    import { ref, watch } from 'vue';
    import { useRoles } from '@/composables/useRoles';
    import Role from '@/enums/Role';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import QueryKey from '@/enums/QueryKey';
    import { useQueryClient } from '@tanstack/vue-query';
    import { usePayrollStore } from '@/stores/payroll';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { computed } from 'vue';

    const props = defineProps<{
        value: number | null;
        editableField: PayrollDataField;
        readOnly: boolean;
        employeeId: number;
        field: string;
        fieldType: string;
        payrollId?: number;
        isApproved: boolean;
    }>();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const queryClient = useQueryClient();
    const payrollStore = usePayrollStore();
    const { t } = useI18n();
    const { hasAnyRole } = useRoles();

    const localValue = ref(props.value);

    watch(
        () => props.value,
        (newVal) => {
            localValue.value = newVal;
        },
    );

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: {
            field: string;
            fieldType: string;
            employeeId: number;
            value: number | null;
        }) =>
            axios.post(
                route('v2.salary.payrolls.field-change', { payroll: props.payrollId }),
                formData,
            ),
        onSuccess: (data) => {
            if (
                localValue.value &&
                localValue.value !== 0 &&
                ((data.data.field === 'hourly_rate' && data.data.fieldType === 'amount') ||
                    ['gage', 'lunch_arrangement_monthly', 'fixed_add_ons_monthly'].includes(
                        data.data.field,
                    ))
            ) {
                payrollStore.openModal(
                    data.data.field,
                    data.data.name,
                    props.employeeId,
                    localValue.value,
                );
            }

            if (data.data.isUpdated) {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLL, selectedCompanyId, props.payrollId],
                });
            }
        },
        onError: (error: any) => {
            console.log('error', error);
            toast.error(error.response.data.error || t('forms.system_error'));
            queryClient.invalidateQueries({
                queryKey: [QueryKey.PAYROLL, selectedCompanyId, props.payrollId],
            });
        },
    });

    function handleBlur() {
        if (localValue.value === props.value) {
            return;
        }

        if (localValue.value && localValue.value < 0) {
            toast.error(t('payrolls.no_negative_value'));
            return;
        }

        mutate({
            field: props.field,
            fieldType: props.fieldType,
            employeeId: props.employeeId,
            value: localValue.value,
        });
    }
</script>

<template>
    <ThousandSeparatorInput
        v-if="localValue !== null"
        v-model="localValue"
        :class="
            hasAnyRole([Role.PAYROLL, Role.SUPER_ADMIN]) &&
            editableField.is_updated &&
            'border-accent/50 bg-accent/10'
        "
        class="min-w-24 text-right"
        :placeholder="localValue.toString()"
        with-decimals
        :disabled="readOnly || isPending || isApproved"
        @blur="handleBlur"
        hide-zeroes
    />
</template>
